import { createSelector } from 'reselect'
import { List, Map } from 'immutable'
import {
  get,
  getIn,
  getOr,
  getInOr,
  getEq,
  some,
  safeToJS,
} from 'utils/immutable'
import {
  referenceParameters,
  features,
  pto,
  switchAndGauge,
  alerts,
} from 'constants/routes'
import { isArray } from 'utils/types'
import { __, identity, compose, prop, isEmpty } from 'ramda'
import { createToJsSelector } from 'selectors/utils'
import {
  VIEW_DASH_ENGINEER_PLUS,
  VIEW_DASH_ENGINEER_M2,
  VIEW_DASH_BUILDER_PLUS,
  VIEW_DASH_BUILDER_M2,
  VIEW_PLUS_VEHICLES,
  VIEW_M2_VEHICLES,
} from 'constants/authorization'
import {
  GLOBAL_ZONE,
  REFERENCE_PARAMETER_ZONE,
  FEATURE_ZONE,
  PTO_ZONE,
  DASHBOARD_ZONE,
  BHM_ECU_ZONE,
  CEEA_PLUS_ECU_ZONE,
  PNEUMATIC_ZONE,
  VEHICLE_TYPE_M2,
  VEHICLE_TYPE_CHEC_PLUS,
} from 'utils/constants'

const getUserState = (state) => state.get('user')

export const appRolesSelector = getIn(['user', 'userData', 'appRoles'])

const userRightsSelector = getInOr(List(), ['user', 'userData', 'appRights'])

export const getAppRoles = createSelector(
  appRolesSelector,
  (appRoles) => safeToJS(appRoles) || []
)

export const userDataSelector = createSelector(
  getUserState,
  getOr(Map(), 'userData')
)

export const getShowReleaseNotes = createSelector(
  userDataSelector,
  get('showReleaseNotes')
)

// Identifies viewer-level users: has pre-prod vehicle access but not full M2 vehicle access.
// Used in deprecated search to show only pre-prod vehicles for this tier.
export const getIsBuilderRole = createSelector(
  userRightsSelector,
  (rights) =>
    rights.has('view_pre_prod_vehicles') && !rights.has(VIEW_M2_VEHICLES)
)

const favoriteVehiclesSelector = (state) =>
  state.getIn(['user', 'favoriteVehicles'])

export const getFavoriteVehicles = createToJsSelector(
  createSelector(favoriteVehiclesSelector, identity)
)

export const getIsProcessingFavoritedVehicle = createSelector(
  getUserState,
  // Use favoritingCount > 0 so the overlay stays visible until ALL concurrent
  // in-flight favorite/unfavorite/update operations complete — not just the first.
  // The boolean processingFavoritedVehicle is kept for reducer compatibility
  // but only this selector is used by App.jsx.
  (user) => (user.get('favoritingCount') || 0) > 0
)

export const isAFavoriteVehicle = createSelector((state, props) => {
  const isFavorite = getEq('serialNumber', props.serialNumber)
  return compose(some(isFavorite), favoriteVehiclesSelector)(state)
}, identity)

const getUserDataFromState = (state) => {
  const user = getUserState(state)

  return {
    name: user.getIn(['userData', 'userName']),
    appRoles: user.getIn(['userData', 'appRoles']),
    eulaAccepted: user.getIn(['userData', 'eulaAccepted']),
    favoriteVehicles: user.get('favoriteVehicles'),
    fetchingFavoriteVehicles: user.get('fetchingFavoriteVehicles'),
    fetchingFavoriteVehiclesError: user.get('fetchingFavoriteVehiclesError'),
    recentlyViewedVehicles: user.get('recentlyViewedVehicles'),
    recentlyViewedSupportParameters: user.get(
      'recentlyViewedSupportParameters'
    ),
    recentlyViewedReferenceParameters: user.get(
      'recentlyViewedReferenceParameters'
    ),
    recentlyViewedFeatures: user.get('recentlyViewedFeatures'),
    fetchingRecentlyViewedData: user.get('fetchingRecentlyViewedData'),
    recentlyViewedDataError: user.get('recentlyViewedDataError'),
  }
}

const getUserDataImmutable = createSelector(
  [getUserDataFromState],
  (user) => user
)

const EMPTY_RECENTLY_VIEWED = List()
const resentlyViewedCollectionSelector = (state, collectionName) =>
  getInOr(EMPTY_RECENTLY_VIEWED, ['user', collectionName], state)
export const getRecentlyViewedVehicles = createSelector(
  resentlyViewedCollectionSelector(__, 'recentlyViewedVehicles'),
  safeToJS
)

export const getRecentlyViewedReferenceParameters = createSelector(
  resentlyViewedCollectionSelector(__, 'recentlyViewedReferenceParameters'),
  safeToJS
)

export const getRecentlyViewedSupportParameters = createSelector(
  resentlyViewedCollectionSelector(__, 'recentlyViewedSupportParameters'),
  safeToJS
)

export const getRecentlyViewedFeatures = createSelector(
  resentlyViewedCollectionSelector(__, 'recentlyViewedFeatures'),
  safeToJS
)

const recentlyViewedMetaData = (state) =>
  new Map()
    .set('userDataError', state.getIn(['user', 'recentlyViewedDataError']))
    .set(
      'fetchingRecentlyViewedData',
      state.getIn(['user', 'fetchingRecentlyViewedData'])
    )

export const getRecentlyViewedMetaData = createSelector(
  recentlyViewedMetaData,
  safeToJS
)

export const getUserData = (state) => {
  const data = getUserDataImmutable(state)

  return {
    name: data.name,
    appRoles: data.appRoles ? data.appRoles.toJS() : [],
    eulaAccepted: data.eulaAccepted,
    favoriteVehicles: data.favoriteVehicles
      ? data.favoriteVehicles.toJS()
      : null,
    fetchingFavoriteVehicles: data.fetchingFavoriteVehicles,
    fetchingFavoriteVehiclesError: data.fetchingFavoriteVehiclesError,
    recentlyViewedVehicles: data.recentlyViewedVehicles
      ? data.recentlyViewedVehicles.toJS()
      : null,
    recentlyViewedSupportParameters: data.recentlyViewedSupportParameters
      ? data.recentlyViewedSupportParameters.toJS()
      : null,
    recentlyViewedReferenceParameters: data.recentlyViewedReferenceParameters
      ? data.recentlyViewedReferenceParameters.toJS()
      : null,
    recentlyViewedFeatures: data.recentlyViewedFeatures
      ? data.recentlyViewedFeatures.toJS()
      : null,
    fetchingRecentlyViewedData: data.fetchingRecentlyViewedData,
    recentlyViewedDataError: data.recentlyViewedDataError,
  }
}

const getAlertState = (state) => {
  const alertState = state.getIn(['user', 'alerts'])
  return alertState
}

export const getAlert = createSelector(getAlertState, safeToJS)

export const getGlobalAlert = createSelector(getAlertState, (alertState) => {
  if (!alertState) return Map()
  const globalAlert = alertState.get(GLOBAL_ZONE)
  return safeToJS(globalAlert)
})

export const getZoneAlerts = createSelector(getAlertState, (zoneAlerts) => {
  if (!zoneAlerts) return List()
  return zoneAlerts
    .valueSeq()
    .filter((alert) => alert.get('zone') !== GLOBAL_ZONE)
    .toJS()
})

// get enabled alerts by zones url dictionary
const alertUrlByZones = {
  [GLOBAL_ZONE]: 'global',
  [REFERENCE_PARAMETER_ZONE]: [referenceParameters, alerts],
  [FEATURE_ZONE]: [features, alerts],
  [PTO_ZONE]: [pto, alerts],
  [DASHBOARD_ZONE]: [switchAndGauge, alerts],
  [BHM_ECU_ZONE]: ['/ecus', alerts],
  [CEEA_PLUS_ECU_ZONE]: ['/ecus', alerts],
  [PNEUMATIC_ZONE]: ['/pneumatics', alerts],
}
const currentPathIncludes = (item, pathname) => pathname.includes(item)

// Zones that require vehicle type filtering on ECU pages
const ecuVehicleTypeByZone = {
  [BHM_ECU_ZONE]: VEHICLE_TYPE_M2,
  [CEEA_PLUS_ECU_ZONE]: VEHICLE_TYPE_CHEC_PLUS,
}

// Extract serial number from pathname like /vehicles/X10587/ecus
const vehicleSerialFromPath = /\/vehicles\/([^/]+)/

const getVehicleTypeFromPath = (state, pathname) => {
  const match = pathname.match(vehicleSerialFromPath)
  if (!match) return null
  const serialNumber = match[1]
  return state.getIn(['truck', 'vehicles', serialNumber, 'vehicleType']) || null
}

// get enabled alerts for current zone/path
export const getEnabledAlertsByZone = createSelector(
  getAlertState,
  (state) => state,
  (_, props) => props,
  (zoneAlerts, state, { pathname }) => {
    if (isEmpty(zoneAlerts)) return List()

    const vehicleType = getVehicleTypeFromPath(state, pathname)

    const enabledZoneAlerts = zoneAlerts
      .valueSeq()
      .filter((zoneAlert) => get('enabled', zoneAlert))
      .reduce((acc, enabledAlert) => {
        const zone = enabledAlert.get('zone')
        const urlArray = alertUrlByZones[zone]
        const hasUrl = isArray(urlArray)
          ? urlArray.some((url) => currentPathIncludes(url, pathname))
          : false

        // For ECU-specific zones, also check vehicle type
        const requiredVehicleType = ecuVehicleTypeByZone[zone]
        if (requiredVehicleType && hasUrl && !pathname.includes(alerts)) {
          if (vehicleType !== requiredVehicleType) return acc
        }

        return hasUrl || urlArray === 'global'
          ? [...acc, safeToJS(enabledAlert)]
          : acc
      }, [])
    return enabledZoneAlerts
  }
)

export const getUserName = createSelector(getUserDataFromState, prop('name'))

const userOrgIdSelector = (state) => state.getIn(['user', 'userData', 'orgId'])

const userOrgNetworkelector = (state) =>
  state.getIn(['user', 'userData', 'orgNetwork'])

export const getUserOrg = createSelector(userOrgIdSelector, safeToJS)

export const getUserOrgNetwork = createSelector(userOrgNetworkelector, safeToJS)

// checks user org against the org id of the config ex: feature, RP etc
export const getIsSameOrgNetwork = createSelector(
  getUserOrgNetwork,
  (_, props) => props,
  (userOrgNetwork, { orgId }) => {
    if (!userOrgNetwork || !orgId) return false
    const isSameOrg = userOrgNetwork.includes(orgId)
    return isSameOrg
  }
)

const userSettingSelector = (state, setting) =>
  getInOr(undefined, ['user', 'userData', 'settings', setting], state)

// if the setting is not defined of has no value, safeToJs will return a null
export const getUserSetting = createSelector(userSettingSelector, safeToJS)

// if user role is engineer or above than show engineering view otherwise show builder view
export const isEngineeringViewEnabled = createSelector(
  userRightsSelector,
  (userRights) =>
    userRights.has(VIEW_DASH_ENGINEER_M2) ||
    userRights.has(VIEW_DASH_ENGINEER_PLUS)
)

export const isBuilderViewEnabled = createSelector(
  userRightsSelector,
  (userRights) =>
    userRights.has(VIEW_DASH_BUILDER_M2) ||
    userRights.has(VIEW_DASH_BUILDER_PLUS)
)

export const getCanEditViewChecPlusVehicle = createSelector(
  userRightsSelector,
  (rights) => rights.has(VIEW_PLUS_VEHICLES)
)

export const getCanEditViewM2Vehicle = createSelector(
  userRightsSelector,
  (rights) => rights.has(VIEW_M2_VEHICLES)
)
